import type { Dir, Pos } from '../grid'

export const ICE_SIZE = 10
export { DELTA, DIRS, type Dir, type Pos, samePos } from '../grid'

/**
 * `#` rock · `.` ice · `o` hole · `S` start · `D` door
 * `^ v < >` arrows: entering one redirects the slide
 * `=` flicker rock: solid on even moves, open on odd moves
 * `~` cracking ice: breaks after you cross it (a hole from then on); stopping on it drops you
 * `K` key: the door stays shut until you have crossed it (only when a level contains a key)
 */
export type Tile = '#' | '.' | 'o' | 'S' | 'D' | '^' | 'v' | '<' | '>' | '=' | '~' | 'K'
export const ARROW_DIR: Partial<Record<Tile, Dir>> = {
  '^': 'up',
  v: 'down',
  '<': 'left',
  '>': 'right',
}

/** A yeti patrols back and forth along `path` (a straight line), one cell per player move. */
export interface Monster {
  path: Pos[]
}

export interface Level {
  id: string
  name: string
  /** Optimal number of moves; test-enforced to equal the BFS solution length. */
  par: number
  rows: readonly string[]
  monsters?: Monster[]
  /** 1-based difficulty tier; drives the level-select grouping. */
  tier?: number
  /** Shown when the level opens — used to introduce a new mechanic. */
  intro?: string
}

/** Everything about one attempt that affects future slides. Deterministic; no randomness. */
export interface RunState {
  pos: Pos
  /** Total moves made this level (drives flicker rocks and yeti patrols). */
  moves: number
  /** Cracked `~` tiles (as position keys). Cleared when you fall. */
  cracked: number[]
  hasKey: boolean
}

export type SlideOutcome = 'stop' | 'fell' | 'caught' | 'locked' | 'door'

export interface StepResult {
  run: RunState
  /** Cells passed through, in order (empty when the move is blocked immediately). */
  path: Pos[]
  outcome: SlideOutcome
  /** Set when the slide ran into an endless arrow loop; such levels are rejected by the generator. */
  looped?: boolean
  /** Set when this move picked up the key. */
  gotKey?: boolean
}

/** Backwards-compatible view used by the basic tests and bots. */
export interface SlideResult {
  path: Pos[]
  end: Pos
  outcome: SlideOutcome
}

export function posKey(p: Pos): number {
  return p.r * ICE_SIZE + p.c
}

export function fromKey(k: number): Pos {
  return { r: Math.floor(k / ICE_SIZE), c: k % ICE_SIZE }
}
